<template>
  <DataTable
    v-model:selectedRows="selectedRows"
    data-key="operatingcostinvoicelines"
    :data="internalCostInvoiceLines"
    :columns="columns"
    :reorderableRows="false"
    :showRowActions="editMode"
    :showSplitView="false"
    :onDelete="handleDelete"
    :onCreate="handleCreate"
    :onRowClick="onRowClick"
    :onRowDblClick="() => {}"
    :rowClass="getInvoiceLineRowClass"
    :prefilterTabs="createOperationCostInvoiceItemsPrefilterTabs(internalCostInvoiceLines)"
  >
    <template #custom-primaryStart>
      <OperatingCostInvoiceImportSelect
        v-if="editMode && !isNew"
        v-model="selectedImportInvoice"
        :importSourceInvoices="props.importSourceInvoices ?? []"
        :currentContractId="props.currentContractId ?? null"
        @change="onImportFromSelected"
      />
    </template>

    <template #custom-primaryActions>
      <div class="flex items-center gap-2">
        <ButtonGroup>
          <Button
            v-if="showImportButton"
            v-tooltip="{ value: t('pages.operatingcostinvoices.importFromLatestLines') }"
            type="button"
            icon="pi pi-download"
            outlined
            @click="importFromLatest"
          />
          <Button icon="pi pi-plus" :label="t('labels.add')" @click="handleCreate" />
        </ButtonGroup>
      </div>
    </template>

    <!-- secondary toolbar actions (Delete + Apply selected) -->
    <template #custom-secondaryActions="{ confirmDeleteSelected }">
      <!-- Delete selected (wie Default) -->
      <Button
        v-if="editMode && !!actualSelectedCount"
        v-tooltip="{ value: t('labels.deleteMany', { count: actualSelectedCount }) }"
        class="ml-4"
        icon="pi pi-trash"
        severity="danger"
        :label="t('labels.deleteMany', { count: actualSelectedCount })"
        :disabled="!profileStore.userProfile?.isSeyboldUser"
        @click="onDeleteActualSelected(confirmDeleteSelected)"
      />

      <!-- Apply selected previous-year lines to current invoice -->
      <Button
        v-if="editMode && !!previousSelectedCount"
        v-tooltip="{
          value: t('pages.operatingcostinvoices.applySelectedLines', {
            count: previousSelectedCount,
          }),
        }"
        class="ml-2"
        icon="pi pi-check"
        :disabled="!profileStore.userProfile?.isSeyboldUser"
        @click="onApplyPreviousSelected"
      />
    </template>
  </DataTable>

  <Dialog v-model:visible="open" modal :header="headerTitle" :style="{ width: '50vw' }">
    <OperatingCostInvoiceLineForm
      v-if="open"
      :uuid="props.invoiceId"
      :editMode="true"
      :isNew="isNew"
      :isCreate="isCreate"
      :formData="selectedRow"
      :initialData="lineInitialData ?? undefined"
      :suggestions="lineSuggestions"
      :disabledFields="lineDisabledFields"
      :onAddCostInvoiceLineToCreate="onAddCostInvoiceLineToCreate"
      :onAddCostInvoiceLineToUpdate="onAddCostInvoiceLineToUpdate"
      :onModalClose="onModalClose"
      :currency="effectiveCurrency"
      :daysInBillingPeriod="props.daysInBillingPeriod"
    />
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, toRefs, watch } from 'vue';
import { v4 } from 'uuid';
import { useI18n } from 'vue-i18n';

import type { LineRowClickEvent } from '@/components/datatable/dataTableTypes';
import { useDetailNavigation } from '@/components/details/hooks/useDetailNavigation';
import {
  columns,
  createOperationCostInvoiceItemsPrefilterTabs,
} from '@/views/operatingcostinvoices/components/operationcostinvoicelines/configs/operatingCostInvoiceLineColumnConfig';
import { useOperatingCostInvoices } from '@/views/operatingcostinvoices/hooks/useOperatingCostInvoices';
import type { OperatingCostInvoiceSimple } from '@/views/operatingcostinvoices/operatingCostInvoiceTypes';
import { useProfileStore } from '@/stores/profile.ts';
import { isPlainRowClick } from '@/utils/dataTable';
import type { CreateOperatingCostInvoiceLineItemV1Dto } from '@/openapi';

import {
  useOperatingCostInvoiceLines,
  useOperatingCostInvoiceLinesConverter,
} from './hooks/useOperatingCostInvoiceLines';
import OperatingCostInvoiceImportSelect from './OperatingCostInvoiceImportSelect.vue';
import OperatingCostInvoiceLineForm from './OperatingCostInvoiceLineForm.vue';
import type {
  OperatingCostInvoiceLineItemSimple,
  OperatingCostInvoiceLineItemWithSource,
} from './operatingCostInvoiceTypes';

const props = defineProps<{
  isNew: boolean;
  editMode: boolean;
  existingOperatingCostInvoiceLines: OperatingCostInvoiceLineItemSimple[];
  currency: string;
  onUpdateLineItemsOnCreate: (data: OperatingCostInvoiceLineItemSimple[]) => void;
  fetchData: () => Promise<void>;
  invoiceId?: string;
  latestInvoiceId?: string;
  latestInvoiceLineItems?: OperatingCostInvoiceLineItemSimple[];
  importSourceInvoices?: OperatingCostInvoiceSimple[];
  currentContractId?: string | null;
  daysInBillingPeriod?: number;
}>();

const { isNew, editMode } = toRefs(props);
const { t } = useI18n();
const profileStore = useProfileStore();

// Ensure currency always has a value (fallback to EUR if not provided)
const effectiveCurrency = computed(() => props.currency || 'EUR');

const { deleteOperatingCostInvoiceLines, createOperatingCostInvoiceLine } =
  useOperatingCostInvoiceLines();
const {
  convertOperatingCostInvoiceLineItemSimpleToCreateOperatingCostInvoiceLineItemDto,
  convertOperatingCostInvoiceLineItemDtoListToOperatingCostInvoiceLineItemSimpleList,
} = useOperatingCostInvoiceLinesConverter();

// useDetailNavigation: if invoiceId is not set, fall back to route
const { subComponentID } = useDetailNavigation({
  uuidFromProp: computed(() => props.invoiceId), // override id
});

const { fetchOperatingCostInvoice } = useOperatingCostInvoices();

const open = ref(false);
const isCreate = ref(false);
const selectedRow = ref<OperatingCostInvoiceLineItemSimple>();
const internalCostInvoiceLines = ref<OperatingCostInvoiceLineItemWithSource[]>([]);

const selectedRows = ref<any[]>([]);
const selectedImportInvoice = ref<OperatingCostInvoiceSimple | null>(null);

const lineInitialData = ref<Partial<OperatingCostInvoiceLineItemSimple> | null>(null);
const lineDisabledFields = ref<Partial<Record<keyof OperatingCostInvoiceLineItemSimple, boolean>>>(
  {},
);

const lineSuggestions = ref<
  Partial<Record<keyof OperatingCostInvoiceLineItemSimple, string[] | Record<string, any>[]>>
>({});

const actualSelectedCount = computed(
  () => selectedRows.value.filter((row) => isActualRow(row)).length,
);

const previousSelectedCount = computed(
  () => selectedRows.value.filter((row) => isPreviousRow(row)).length,
);

const headerTitle = computed(
  () => `${isCreate.value ? t('labels.new') : t('labels.edit')} ${t('text.invoiceLineItem')}`,
);

// Helper: use sfmPositionId as identity key
const getInternalKey = (line: OperatingCostInvoiceLineItemSimple): string => line.sfmPositionId;

// are there any lines from previous invoice not yet imported?
const hasImportableLines = computed(() => {
  const previousLines = props.latestInvoiceLineItems ?? [];
  if (!previousLines.length) return false;

  // all keys of actual invoice (actual + ggf. previous)
  const currentKeys = new Set(
    internalCostInvoiceLines.value.map((line) => getInternalKey(line)).filter((k) => k.length > 0),
  );

  // is there at least one last year`s item with a new key?
  return previousLines.some((prev) => {
    const key = getInternalKey(prev);
    return key.length > 0 && !currentKeys.has(key);
  });
});

const hasPreviousInvoice = computed(() => {
  const hasId = !!props.latestInvoiceId;
  const hasLineItems = (props.latestInvoiceLineItems?.length ?? 0) > 0;
  return hasId && hasLineItems;
});

const showImportButton = computed(
  () => editMode.value && hasPreviousInvoice.value && hasImportableLines.value,
);

function mergeImportedLines(dtoLines: OperatingCostInvoiceLineItemSimple[]) {
  if (!dtoLines.length) {
    console.warn('[Lines] No line items to import');
    return;
  }

  // Maps existing lines by their internal key
  const existingByKey = new Map<string, OperatingCostInvoiceLineItemSimple>(
    internalCostInvoiceLines.value.map((line) => [getInternalKey(line), line]),
  );

  // Mark existing lines as 'actual'
  const merged: OperatingCostInvoiceLineItemWithSource[] = internalCostInvoiceLines.value.map(
    (line) => ({
      ...line,
      invoiceItemSource: line.invoiceItemSource ?? 'actual',
    }),
  );

  // Only add new keys as 'previous'
  for (const prev of dtoLines) {
    const key = getInternalKey(prev);
    if (!key) continue;
    if (existingByKey.has(key)) continue; // new invoice wins

    merged.push({
      ...prev,
      id: v4(),
      version: 0,
      invoiceItemSource: 'previous',
    });
  }

  internalCostInvoiceLines.value = merged;
}

// Import from the invoice selected in CascadeSelect
const onImportFromSelected = () => {
  const inv = selectedImportInvoice.value;
  if (!inv) return;

  const lines = inv.lineItems ?? [];
  mergeImportedLines(lines);
};

/** Called when creating a line on a not-yet-persisted invoice */
const onAddCostInvoiceLineToCreate = (item: CreateOperatingCostInvoiceLineItemV1Dto) => {
  internalCostInvoiceLines.value.push({
    ...(item as any),
    id: v4(),
    version: 0,
    invoiceItemSource: 'actual', // lines created here are already "actual"
  });
};

const onAddCostInvoiceLineToUpdate = (item: OperatingCostInvoiceLineItemSimple) => {
  const lines = internalCostInvoiceLines.value;
  const index = lines.findIndex((x) => x.id === item.id);

  if (index !== -1) {
    const existing = lines[index] as OperatingCostInvoiceLineItemWithSource;
    internalCostInvoiceLines.value[index] = {
      ...existing,
      ...item,
      invoiceItemSource: existing.invoiceItemSource ?? 'actual',
    };
  } else {
    lines.push({ ...item, invoiceItemSource: 'actual' });
  }
};

function onDeleteActualSelected(confirmDeleteSelected: () => void) {
  const actualRows = selectedRows.value.filter((row) => isActualRow(row));

  if (!actualRows.length) {
    return;
  }

  // Restrict selection to deletable (Actual) rows only
  // DataTable uses this selection for its internal delete logic
  selectedRows.value = actualRows;

  // Now trigger the DataTable's confirm + delete flow
  confirmDeleteSelected();
}

// Helper: Map a table row back to the underlying line item
function findLineItemForRow(row: any): OperatingCostInvoiceLineItemSimple | undefined {
  const id = row?.id?.meta?.('rawvalue') ?? row?.id;
  if (!id) return undefined;
  return internalCostInvoiceLines.value.find((x) => x.id === id);
}

// Helper: Check source flags
function isActualRow(row: any): boolean {
  const item = findLineItemForRow(row);
  // Treat missing flag as "Actual" (current)
  return !item?.invoiceItemSource || item.invoiceItemSource === 'actual';
}

const applyValue = <K extends keyof OperatingCostInvoiceLineItemSimple>(
  value: OperatingCostInvoiceLineItemSimple[K],
  key: K,
) => {
  if (value && lineInitialData.value) {
    lineInitialData.value[key] = value;
  }
};

function buildInitialHints() {
  lineInitialData.value = { ...(lineInitialData.value ?? {}) };

  // Get the latest actual line item and pre-fill with its values
  const actualLines = internalCostInvoiceLines.value.filter(
    (line) => line.invoiceItemSource === 'actual',
  );
  if (actualLines.length === 0) return;

  // Get the last actual line (most recently added/created)
  const latestLine = actualLines[actualLines.length - 1];

  applyValue(latestLine.areaDesignation, 'areaDesignation');
  applyValue(latestLine.customerArea, 'customerArea');
  applyValue(latestLine.totalArea, 'totalArea');
}

function isPreviousRow(row: any): boolean {
  const item = findLineItemForRow(row);
  return item?.invoiceItemSource === 'previous';
}

async function onApplyPreviousSelected() {
  // Filter only on selected previous lines
  const previousRows = selectedRows.value.filter((row) => isPreviousRow(row));

  if (!previousRows.length) {
    return;
  }

  // Persist to backend (creates real Actual lines on current invoice)
  await applySelectedLines(previousRows);

  // Optional: Auswahl leeren
  selectedRows.value = [];
}

const onModalClose = async (reopenForNextEntry: boolean = false) => {
  open.value = false;
  isCreate.value = false;

  if (!props.isNew && typeof props.fetchData === 'function') {
    await props.fetchData();
    // Explicitly sync after fetch — the watch alone cannot guarantee timing here
    internalCostInvoiceLines.value = (props.existingOperatingCostInvoiceLines ?? []).map(
      (line) => ({ ...line, invoiceItemSource: 'actual' as const }),
    );
  }

  if (reopenForNextEntry) {
    // Reset state for next entry
    selectedRow.value = undefined;
    selectedRows.value = [];
    lineInitialData.value = null;
    lineDisabledFields.value = {};

    // Prepare hints for the next line
    buildInitialHints();

    // Set up state for creating a new line
    isCreate.value = true;

    // Reopen the modal with fresh state
    open.value = true;
  }
};

const onRowClick = (rowEvent: LineRowClickEvent) => {
  if (!isPlainRowClick(rowEvent)) {
    return;
  }
  const rowId = rowEvent.data.id._meta.rawvalue;
  const line = internalCostInvoiceLines.value.find((x) => x.id === rowId);

  if (!line) return;

  if (!props.isNew && line.invoiceItemSource === 'previous') {
    // Existing invoice + previous line → create new line with prefilled values
    isCreate.value = true;

    const { id: _id, version: _version, invoiceItemSource: _src, ...rest } = line;

    // Prefill all editable fields from previous line
    lineInitialData.value = { ...rest };

    // Internal key should not be changed when cloning from previous
    lineDisabledFields.value = { sfmPositionName: true };

    buildInitialHints();

    // No existing row to update
    selectedRow.value = undefined;
  } else {
    // Normal edit of an actual line
    isCreate.value = false;
    selectedRow.value = { ...line };
    lineInitialData.value = null;
    lineDisabledFields.value = {};
  }

  open.value = true;
};

const handleCreate = () => {
  isCreate.value = true;
  selectedRow.value = undefined;
  lineInitialData.value = null;
  lineDisabledFields.value = {};

  buildInitialHints();

  open.value = true;
};

const handleDelete = async (selectedIdsToDelete: string[]) => {
  internalCostInvoiceLines.value = internalCostInvoiceLines.value.filter(
    (x) => !selectedIdsToDelete.includes(x.id),
  );

  if (!props.isNew) {
    try {
      const invoiceID = props.invoiceId ?? subComponentID.value;
      await deleteOperatingCostInvoiceLines(String(invoiceID), selectedIdsToDelete);
    } finally {
      await props.fetchData();
    }
  }
};

const getInvoiceLineRowClass = (row: any): string => {
  const item = findLineItemForRow(row);
  return item?.invoiceItemSource === 'previous' ? 'invoice-line-previous' : '';
};

/**
 * Import + merge:
 * - existing lines are always kept (invoiceItemSource = 'actual')
 * - previous-year lines from latestInvoiceLineItems are added if their sfmPositionName is new
 * - imported lines get invoiceItemSource = 'previous'
 */
const importFromLatest = () => {
  const dtoLines = props.latestInvoiceLineItems ?? [];
  if (!dtoLines.length) {
    console.warn('No line items in latest invoice');
    return;
  }

  mergeImportedLines(dtoLines);
};

const importFromInvoice = async (invoiceId: string) => {
  try {
    const dto = await fetchOperatingCostInvoice(invoiceId);
    const dtoLines =
      convertOperatingCostInvoiceLineItemDtoListToOperatingCostInvoiceLineItemSimpleList(
        dto.lineItems ?? [],
      );

    mergeImportedLines(dtoLines);
  } catch (err) {
    console.error('[Lines] Failed to import from invoice', invoiceId, err);
  }
};

defineExpose({
  importFromInvoice,
});

/**
 * Persist selected previous-year lines to current invoice
 * and reload from backend afterwards.
 */
const applySelectedLines = async (selectedRows: any[]) => {
  const invoiceID = props.invoiceId ?? subComponentID.value;
  if (!invoiceID) {
    console.warn('[Lines] Cannot apply selection without invoiceId');
    return;
  }

  const selectedIds = selectedRows
    ?.map((row) => row.id?.meta?.('rawvalue') ?? row.id)
    .filter(Boolean) as string[];

  const linesToPersist = internalCostInvoiceLines.value.filter(
    (line) => selectedIds.includes(line.id) && line.invoiceItemSource === 'previous',
  );

  if (!linesToPersist.length) {
    console.log('[Lines] No previous lines selected to apply');
    return;
  }

  for (const line of linesToPersist) {
    // Strip invoiceItemSource before converting to DTO
    const { invoiceItemSource: _src, ...simple } = line;

    const dto: CreateOperatingCostInvoiceLineItemV1Dto =
      convertOperatingCostInvoiceLineItemSimpleToCreateOperatingCostInvoiceLineItemDto(simple);

    await createOperatingCostInvoiceLine(String(invoiceID), dto);
  }

  // After persisting, reload full invoice → new lines come back as 'actual'
  await props.fetchData();
};

// Keep parent in sync for "new invoice" mode only
const stripSource = (
  lines: OperatingCostInvoiceLineItemWithSource[],
): OperatingCostInvoiceLineItemSimple[] =>
  lines.map(({ invoiceItemSource: _invoiceItemSource, ...rest }) => rest);

watch(
  internalCostInvoiceLines,
  (newValues) => {
    if (props.isNew) {
      props.onUpdateLineItemsOnCreate(stripSource(newValues));
    }
  },
  { immediate: true },
);

// Initialize / refresh internal lines from props (always as 'actual')
watch(
  () => props.existingOperatingCostInvoiceLines,
  (newVal) => {
    internalCostInvoiceLines.value = (newVal ?? []).map((line) => ({
      ...line,
      invoiceItemSource: 'actual',
    }));
  },
  { immediate: true },
);
</script>

<!-- frontend-zelos-next/src/views/operatingcostinvoices/components/operationcostinvoicelines/OperatingCostInvoiceLines.vue -->
<style lang="css">
@config "../../../../../tailwind.config.js";
@reference "tailwindcss";
.invoice-line-previous .table-cell {
  font-style: italic;
  @apply text-gray-500;
}
</style>
